import type { AppDefinition, SessionRecord } from '@web/api/types'
import {
  GallerySkeleton,
  groupedApps,
  OwnershipGlyph,
} from '@web/components/AppGallery'
import { SettingsSheet } from '@web/components/mobile/SettingsScreen'
import { statusTone } from '@web/components/mobile/sessions'
import { CircleButton, TopBar } from '@web/components/mobile/TopBar'
import { Icon } from '@web/components/ui/icons'
import { appIcon } from '@web/lib/appIcon'
import { navigateWorkspace as navigate, useIdentity } from '@web/state/identity'

/** Let hyphenated names ("Know-Your-Customer") wrap at the hyphen, not mid-word. */
export function breakable(title: string): string {
  return title.replace(/-(?=\S)/g, '-​')
}

/** The phone home screen presents the app catalogue. */
export function MobileHome({
  apps,
  loading,
  launching,
  error,
  onRetry,
  onLaunch,
  settingsOpen = false,
  onCloseSettings = () => {},
}: {
  apps: AppDefinition[]
  loading: boolean
  launching: string | null
  error: string
  onRetry: () => void
  onLaunch: (app: AppDefinition) => void
  /** The settings drawer sits over the home, opened from the gear or `/settings`. */
  settingsOpen?: boolean
  onCloseSettings?: () => void
}) {
  const { state } = useIdentity()
  const groups = groupedApps(apps)
  return (
    <main className="m-screen" aria-label="Canopy home">
      <TopBar
        align="left"
        title={<h1 className="m-topbar-brand">Canopy</h1>}
        subtitle={state?.current_org?.name ?? 'Your personal workspace'}
        right={
          <>
            <CircleButton
              icon="clock"
              label="Sessions"
              onClick={() => navigate('/sessions')}
            />
            <CircleButton
              icon="settings"
              label="Settings"
              expanded={settingsOpen}
              onClick={() => navigate('/settings')}
            />
          </>
        }
      />
      <div className="m-scroll">
        {loading && !apps.length && <GallerySkeleton />}
        {groups.map(({ name, items }) => (
          <section className="m-section" aria-label={name} key={name}>
            <h2 className="m-section-title">{name}</h2>
            <div className="m-tiles">
              {items.map((item) => (
                <button
                  type="button"
                  key={`${item.id}:${item.digest ?? item.version}`}
                  className="m-tile"
                  aria-label={`Open ${item.title}`}
                  disabled={!!launching}
                  onClick={() => onLaunch(item)}
                >
                  <span className="m-tile-art">
                    <img
                      className="m-tile-icon"
                      src={appIcon(item.iconUrl, 128)}
                      width={72}
                      height={72}
                      decoding="async"
                      alt=""
                    />
                    {item.ownership && (
                      <span
                        className={`m-tile-badge m-tile-badge-${item.ownership}`}
                        role="img"
                        aria-label={
                          item.ownership === 'personal'
                            ? 'Created by you'
                            : 'Shared with you'
                        }
                      >
                        <OwnershipGlyph ownership={item.ownership} size={12} />
                      </span>
                    )}
                    {launching === item.id && (
                      <span className="m-tile-loading">
                        <Icon name="loader" size={22} className="spin" />
                      </span>
                    )}
                  </span>
                  <span className="m-tile-title">{breakable(item.title)}</span>
                </button>
              ))}
            </div>
          </section>
        ))}
        {!loading && !apps.length && !error && (
          <p className="m-feedback">
            No agents are available in this organization yet.
          </p>
        )}
        {error && (
          <div className="m-error" role="alert">
            <p>{error}</p>
            <button type="button" className="btn" onClick={onRetry}>
              Try again
            </button>
          </div>
        )}
      </div>
      <SettingsSheet open={settingsOpen} onClose={onCloseSettings} />
    </main>
  )
}

/** An app icon with the session's status dot in the corner. */
export function SessionAvatar({
  src,
  title,
  status,
  size = 36,
}: {
  src?: string
  title: string
  status: SessionRecord['status']
  size?: number
}) {
  return (
    <span className={`m-avatar ${size > 36 ? 'm-avatar-lg' : ''}`}>
      {src ? (
        <img
          src={appIcon(src, 64)}
          width={size}
          height={size}
          decoding="async"
          alt={`${title} agent`}
        />
      ) : (
        <span className="m-avatar-fallback" aria-hidden="true" />
      )}
      <span
        className={`m-status-dot tone-${statusTone(status)}`}
        title={status}
      />
    </span>
  )
}
